import type { Plugin, SegmentEvent } from '@segment/analytics-next'
import * as segmentEventFields from '../generated/segmentEventRequiredFields'

const { segmentTrackEventRequiredFields } = segmentEventFields

export const getTrackEventAllowedFields = (
  eventName: string
): readonly string[] => {
  if ('segmentTrackEventAllowedFields' in segmentEventFields) {
    const allowedFieldsByEvent = (
      segmentEventFields as {
        segmentTrackEventAllowedFields: Record<string, readonly string[]>
      }
    ).segmentTrackEventAllowedFields

    const allowed = allowedFieldsByEvent[eventName]
    if (allowed?.length) {
      return allowed
    }
  }

  return (
    segmentTrackEventRequiredFields[
      eventName as keyof typeof segmentTrackEventRequiredFields
    ] ?? []
  )
}

export const isSegmentEventFieldsModuleStale = (
  moduleExports: Record<string, unknown>
): boolean => !('segmentTrackEventAllowedFields' in moduleExports)

let hasWarnedAboutStaleSegmentEventFields = false

export const warnIfSegmentEventFieldsAreStale = (): void => {
  if (hasWarnedAboutStaleSegmentEventFields || !import.meta.env.DEV) {
    return
  }

  if (
    !isSegmentEventFieldsModuleStale(
      segmentEventFields as Record<string, unknown>
    )
  ) {
    return
  }

  hasWarnedAboutStaleSegmentEventFields = true

  console.warn(
    '[analytics] segmentEventRequiredFields.ts is out of date. Restart `npm start` to regenerate it. Validation continues with required fields only.'
  )
}

warnIfSegmentEventFieldsAreStale()

export const isAnalyticsDevLoggingEnabled = (): boolean =>
  process.env.SEGMENT_WARNS_ENABLED === 'true'

export const logAnalyticsEventPayload = (
  eventLabel: string,
  properties: { [key: string]: unknown }
): void => {
  if (!isAnalyticsDevLoggingEnabled()) {
    return
  }

  console.log(`[analytics] "${eventLabel}":`, properties)
}

export const getMissingEventProperties = (
  requiredProperties: readonly string[],
  properties: { [key: string]: unknown }
): string[] =>
  requiredProperties.filter(property => {
    const value = properties[property]

    return !(property in properties) || value === undefined
  })

export const getExtraEventProperties = (
  allowedProperties: readonly string[],
  properties: { [key: string]: unknown }
): string[] =>
  Object.keys(properties).filter(
    property => !allowedProperties.includes(property)
  )

export const logMissingRequiredProperties = (
  eventLabel: string,
  requiredProperties: readonly string[],
  allowedProperties: readonly string[],
  properties: { [key: string]: unknown }
): void => {
  if (!isAnalyticsDevLoggingEnabled()) {
    return
  }

  logAnalyticsEventPayload(eventLabel, properties)

  const missingProperties = getMissingEventProperties(
    requiredProperties,
    properties
  )

  if (missingProperties.length > 0) {
    console.warn(
      `[analytics] "${eventLabel}" is missing properties:`,
      missingProperties
    )
  }

  const extraProperties = getExtraEventProperties(allowedProperties, properties)

  if (extraProperties.length > 0) {
    console.warn(
      `[analytics] "${eventLabel}" has extra properties:`,
      extraProperties
    )
  }
}

const logMissingEventProperties = (event: SegmentEvent): void => {
  if (event.type !== 'track' || !event.event) {
    return
  }

  const requiredProperties =
    segmentTrackEventRequiredFields[
      event.event as keyof typeof segmentTrackEventRequiredFields
    ]
  if (!requiredProperties?.length) {
    return
  }

  logMissingRequiredProperties(
    event.event,
    requiredProperties,
    getTrackEventAllowedFields(event.event),
    event.properties ?? {}
  )
}

export const missingEventPropertiesPlugin: Plugin = {
  name: 'Missing Event Properties Interceptor',
  type: 'before',
  version: '1.0.0',
  isLoaded: () => true,
  load: () => Promise.resolve(),
  track: ctx => {
    logMissingEventProperties(ctx.event)
    return ctx
  },
}
